import { Group, ScrollArea, Stack } from '@mantine/core';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterPanelGroup, FilterPanelToggle } from '../../../components/filters/filter-panel-group';
import { FilterPanelGroupMultiSelect } from '../../../components/filters/filter-panel-group-multiselect';
import { FilterPanelHeader } from '../../../components/filters/filter-panel-header';
import { Divider } from '../../../ui-library/divider/divider';
import { Text } from '../../../ui-library/typography/typography';
import { SmallSquareButton } from '../../../components/small-square-button/small-square-button';
import { useOutcomes } from '../../../api/strategy-client/strategy-client.hooks';
import { StatusLabel } from '../initiative-list/filters/status-label';
import { primaryBase, yellowBase, redBase, skyDark } from '../../../styles/design-tokens';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import {
  setStatusFilters,
  setOnlyBookmarkedFilter,
  setIncludeEmptyFilter,
  setOutcomeTeamsFilters,
} from '../../../store/strategy-store/strategy-store.actions';
import type { FilterGroup } from '../../../components/filters/filter-panel.type';

// Stable fallback identity — see the note in outcomes-main-section.tsx.
const NO_TEAM_FILTER: string[] = [];

export function OutcomesFilters({ setShow, title }: { setShow: (show: boolean) => void; title?: string }) {
  const i18n = useTranslation();
  const { outcomes } = useOutcomes();
  const statusFiltersFromStore = useStrategyStore((state) => state.filters.status);
  const onlyBookmarkedFromStore = useStrategyStore((state) => state.filters.only_bookmarked);
  const includeEmptyFromStore = useStrategyStore((state) => state.filters.include_empty);
  // Read/written straight through the store rather than mirrored into local state like the
  // three controls above — a new filter does not need two sources of truth.
  const selectedTeamIds = useStrategyStore((state) => state.filters.outcome_teams) ?? NO_TEAM_FILTER;
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // Initialize selectedStatuses from store
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    statusFiltersFromStore || ['on_track', 'at_risk', 'off_track', 'unknown'],
  );

  // Initialize bookmark filter from store
  const [onlyBookmarked, setOnlyBookmarked] = useState<boolean>(onlyBookmarkedFromStore || false);

  // Initialize include empty filter from store
  const [includeEmpty, setIncludeEmpty] = useState<boolean>(includeEmptyFromStore || false);

  // Sync with store when selection changes
  useEffect(() => {
    setStatusFilters(selectedStatuses);
  }, [selectedStatuses]);

  // Sync bookmark filter with store
  useEffect(() => {
    setOnlyBookmarkedFilter(onlyBookmarked);
  }, [onlyBookmarked]);

  // Sync include empty filter with store
  useEffect(() => {
    setIncludeEmptyFilter(includeEmpty);
  }, [includeEmpty]);

  // Get unique status values
  const statusOptions = [
    {
      value: 'on_track',
      label: <StatusLabel color={primaryBase} label={i18n.t('strategy.initiatives.statuses.on_track')} />,
      count: outcomes.filter((o) => o.status?.by_tasks?.status === 'on_track').length,
    },
    {
      value: 'at_risk',
      label: <StatusLabel color={yellowBase} label={i18n.t('strategy.initiatives.statuses.at_risk')} />,
      count: outcomes.filter((o) => o.status?.by_tasks?.status === 'at_risk').length,
    },
    {
      value: 'off_track',
      label: <StatusLabel color={redBase} label={i18n.t('strategy.initiatives.statuses.off_track')} />,
      count: outcomes.filter((o) => o.status?.by_tasks?.status === 'off_track').length,
    },
    {
      value: 'unknown',
      label: <StatusLabel color={skyDark} label={i18n.t('strategy.initiatives.statuses.unknown')} />,
      count: outcomes.filter((o) => !o.status?.by_tasks?.status).length,
    },
  ];

  // Option list is derived from ALL loaded outcomes, not the filtered set, so the choices
  // stay stable while the user narrows. Deduped by id because the union across outcomes
  // legitimately repeats a team; each outcome's own `teams` array is already unique.
  const teamOptions = useMemo(() => {
    const byId = new Map<string, string>();

    outcomes.forEach((outcome) => {
      (outcome.teams ?? []).forEach((team) => {
        if (!byId.has(team.id)) {
          // Store the label we will actually display. `toComboboxData` substitutes the
          // option value for a blank label downstream, so without this the sort below
          // orders on '' while the dropdown shows a UUID — the list would come out
          // misordered rather than just oddly labelled.
          byId.set(team.id, team.name || team.id);
        }
      });
    });

    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [outcomes]);

  const teamFilterGroup: FilterGroup = {
    name: i18n.t('strategy.filters.teams'),
    icon: 'groups',
    options: teamOptions,
    selectedValues: selectedTeamIds,
    onSelectionChange: setOutcomeTeamsFilters,
    // `optional` drives `clearable` on the Mantine control. `multiSelect` is inert on this
    // direct-render path (only FilterPanel's dispatch reads it) but documents intent.
    optional: true,
    multiSelect: true,
    searchable: true,
  };

  const filterGroups = [
    {
      id: 'status',
      name: i18n.t('strategy.filters.status'),
      icon: 'filter_tilt_shift',
      options: statusOptions,
      selectedValues: selectedStatuses,
      onSelectionChange: setSelectedStatuses,
      showAllLabel: i18n.t('strategy.filters.all_statuses'),
    },
  ];

  const toggleCollapsed = (groupId: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const isGroupCollapsed = (groupId: string): boolean => {
    return collapsedGroups[groupId];
  };

  const isAllSelected = (group: (typeof filterGroups)[0]): boolean => {
    return group.selectedValues.length > 0 && group.selectedValues.length === group.options.length;
  };

  const handleAllChange = (group: (typeof filterGroups)[0], isChecked: boolean): void => {
    group.onSelectionChange(isChecked ? group.options.map((opt) => opt.value) : []);
  };

  const handleIndividualChange = (group: (typeof filterGroups)[0], value: string, isChecked: boolean): void => {
    const newValues = isChecked ? [...group.selectedValues, value] : group.selectedValues.filter((v) => v !== value);
    group.onSelectionChange(newValues);
  };

  const handleReset = () => {
    setSelectedStatuses(['on_track', 'at_risk', 'off_track', 'unknown']);
    setOnlyBookmarked(false);
    setIncludeEmpty(false);
    setOutcomeTeamsFilters([]);
  };

  return (
    <Stack data-bf="strategy-filter-outcomes" gap="md" h="100%">
      <Group justify="space-between">
        <Text size="medium" weight="medium">
          {title || i18n.t('common.outcomes')}
        </Text>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Divider />
      <FilterPanelHeader onResetFilters={handleReset} />
      <Divider />

      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        <Stack gap="md">
          <FilterPanelToggle
            toggle={{
              id: 'include_empty',
              name: i18n.t('strategy.filters.include_empty'),
              icon: 'folder_open',
              selectedValue: includeEmpty,
              onSelectionChange: setIncludeEmpty,
            }}
            hasDivider
          />
          <FilterPanelToggle
            toggle={{
              id: 'only_bookmarked',
              name: i18n.t('strategy.filters.bookmarked_only'),
              icon: 'bookmark_added',
              selectedValue: onlyBookmarked,
              onSelectionChange: setOnlyBookmarked,
            }}
            hasDivider
          />
          {filterGroups.map((group) => (
            <FilterPanelGroup
              key={group.id}
              group={group}
              toggleCollapsed={(group) => toggleCollapsed(group.id)}
              isCollapsed={isGroupCollapsed(group.id)}
              isAllSelected={isAllSelected(group)}
              handleAllChange={handleAllChange}
              handleIndividualChange={handleIndividualChange}
              hasDivider={false}
            />
          ))}
          <FilterPanelGroupMultiSelect
            group={teamFilterGroup}
            handleMultiSelectChange={(group, values) => group.onSelectionChange(values)}
            hasDivider={false}
          />
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
